/* ---------- 1️⃣  Channel multipliers (unchanged) ---------- */

import { blg } from "@aix/apiutils/src/logging"
import type { EngineMeta, RateKey, RateUsage } from "@aix/apiutils/src/models/pricing"
import { MODEL_METADATA } from "@aix/apiutils/src/models/pricing"
import { replicateImageModelPricing, replicateModelPricing } from "@aix/apiutils/src/models/replicate/pricing"

/** Minimal shape of a commercial-registry entry — only what the tier resolver needs. */
interface CommercialEntry {
  rate: number | ((params: Record<string, unknown>) => number)
  unit: string
}
import { mapValues, reduce } from "lodash-es"
import type { IAllModelTypes } from "@aix/apiutils/src/llms/enums"
import type {
  OpenAIImageGenerationModelType,
  FluxImageGenerationModelType,
  GoogleImageGenerationModelType,
  NvidiaImageGenerationModelType,
  KlingAIVideoGenerationModelType,
} from "@aix/apiutils/src/image/enums"
import { BadDataError } from "@aix/apiutils/src/trpcErrors"
import { ZodError } from "zod"
import { ESubscriptionTier } from "./enums"

// Define a comprehensive type that includes all model types
type AllSupportedModelTypes =
  | IAllModelTypes
  | OpenAIImageGenerationModelType
  | FluxImageGenerationModelType
  | GoogleImageGenerationModelType
  | NvidiaImageGenerationModelType
  | KlingAIVideoGenerationModelType

export const FREE_USER_CREDITS = 0
export const MAX_TOPUP_CREDIT_LIMIT = 7200

export const USD_PER_CREDIT = 0.02

export enum EFeaturesVidmax {
  AI_MOTION = "AI_MOTION",
  AI_IMAGE = "AI_IMAGE",
  AI_VIDEO = "AI_VIDEO",
  AI_AUDIO = "AI_AUDIO",
  AI_TEXT = "AI_TEXT",
  AI_IMAGE_GENERATION = "AI_IMAGE_GENERATION",
  AI_VIDEO_GENERATION = "AI_VIDEO_GENERATION",
  AI_AUTOMATION = "AI_AUTOMATION",
  AI_AUTOMATED_SERIES = "AI_AUTOMATED_SERIES",
}

export const FEATURE_DETAIL_MAP: Record<EFeaturesVidmax, { subscriptionType: ESubscriptionTier; featureName: string }> =
  {
    [EFeaturesVidmax.AI_MOTION]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Motion",
    },
    [EFeaturesVidmax.AI_IMAGE]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Image",
    },
    [EFeaturesVidmax.AI_VIDEO]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Video",
    },
    [EFeaturesVidmax.AI_AUDIO]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Audio",
    },
    [EFeaturesVidmax.AI_TEXT]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Text",
    },
    [EFeaturesVidmax.AI_IMAGE_GENERATION]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Image Generation",
    },
    [EFeaturesVidmax.AI_VIDEO_GENERATION]: {
      subscriptionType: ESubscriptionTier.Starter,
      featureName: "AI Video Generation",
    },
    [EFeaturesVidmax.AI_AUTOMATION]: {
      subscriptionType: ESubscriptionTier.Creator,
      featureName: "AI Automation",
    },
    [EFeaturesVidmax.AI_AUTOMATED_SERIES]: {
      subscriptionType: ESubscriptionTier.Creator,
      featureName: "AI Automated Series",
    },
  }

export const _checkUserFeatureAccess = (feature: EFeaturesVidmax, userESubscriptionTier?: ESubscriptionTier) => {
  const subscriptionNeeded = FEATURE_DETAIL_MAP[feature].subscriptionType
  const featureName = FEATURE_DETAIL_MAP[feature].featureName
  const hasAccess = getSubscriptionTierLevel(userESubscriptionTier) >= getSubscriptionTierLevel(subscriptionNeeded)
  return { hasAccess, subscriptionNeeded, featureName }
}

// Add helper to determine tier levels
export const getSubscriptionTierLevel = (subscriptionType?: ESubscriptionTier): number => {
  const tiers = {
    [ESubscriptionTier.Free]: 0,
    [ESubscriptionTier.Starter]: 1,
    [ESubscriptionTier.Creator]: 2,
    [ESubscriptionTier.Pro]: 3,
    // Ads Platform Tiers (same level as viral equivalents)
    [ESubscriptionTier.StarterPlan]: 1,
    [ESubscriptionTier.BusinessPlan]: 2,
    [ESubscriptionTier.AgencyPlan]: 3,
  }
  return subscriptionType ? (tiers[subscriptionType] ?? 0) : 0
}

export enum EChannelCostType {
  API = "API",
  TOOL = "TOOL",
  WORKFLOW = "WORKFLOW",
}
export enum EUsageAddType {
  PURCHASE = "PURCHASE",
  BONUS = "BONUS",
  REFUND = "REFUND",
}

// Which dollar fact a vm_AssetJob's cost came from. See brain/vidmax/feature-specs/provider-billing-truth.md
// REGISTRY_ESTIMATE = our registry rate × REQUESTED params. Submit-time intent.
// OUTPUT_MEASURED   = our registry rate × the output the provider actually returned. Our rate,
//                     their quantity — catches output variance, NOT rate drift. Never invoice-grade.
// ASSET_PROBED      = OUTPUT_MEASURED where the quantity was measured off the persisted asset.
// PROVIDER_BILLED   = exact USD imported from the provider's billing ledger. The only invoice truth.
// PROVIDER_REPORTED = DEPRECATED former name of OUTPUT_MEASURED. Never written by new code; still
//                     present on historical rows and in model-validation evidence, so reads must
//                     go through `isOutputMeasuredCostSource`.
export enum ECostSource {
  REGISTRY_ESTIMATE = "REGISTRY_ESTIMATE",
  PROVIDER_REPORTED = "PROVIDER_REPORTED",
  ASSET_PROBED = "ASSET_PROBED",
  OUTPUT_MEASURED = "OUTPUT_MEASURED",
  PROVIDER_BILLED = "PROVIDER_BILLED",
}

/** Cost sources meaning "registry rate × a quantity the provider actually produced". */
const OUTPUT_MEASURED_COST_SOURCES = new Set<string>([
  ECostSource.OUTPUT_MEASURED,
  ECostSource.PROVIDER_REPORTED,
  ECostSource.ASSET_PROBED,
])

/**
 * Whether a stored `costSource` means output-measured, including the deprecated
 * `PROVIDER_REPORTED` spelling. Every reader must use this rather than `=== PROVIDER_REPORTED`,
 * or historical rows and new rows answer the same question differently.
 */
export const isOutputMeasuredCostSource = (costSource: string | null | undefined): boolean =>
  costSource != null && OUTPUT_MEASURED_COST_SOURCES.has(costSource)

/**
 * Where a job stands against its provider's billing ledger.
 *
 * `AGGREGATE_ONLY` is a capability statement, not a failure: Replicate and Kie expose no
 * per-request billed dollars, so those jobs reconcile at the daily account level and must never be
 * displayed as invoice-matched. `LEGACY_ID_UNAVAILABLE` marks a pre-deploy row with no provider ID
 * — permanently unmatchable, and never to be "repaired" by timestamp or model similarity.
 */
export enum EBillingReconciliationStatus {
  PENDING = "PENDING",
  MATCHED = "MATCHED",
  MISMATCHED = "MISMATCHED",
  AGGREGATE_ONLY = "AGGREGATE_ONLY",
  PROVIDER_UNAVAILABLE = "PROVIDER_UNAVAILABLE",
  LEGACY_ID_UNAVAILABLE = "LEGACY_ID_UNAVAILABLE",
}

/** The cost columns `selectJobCostUsd` reads. Accepts a raw `vm_AssetJob` row. */
export interface IJobCostView {
  providerBilledCostUsd?: number | null
  outputMeasuredCostUsd?: number | null
  estimatedCostUsd?: number | null
  /** Deprecated alias of `outputMeasuredCostUsd`, dual-written during the VM-1012 migration. */
  actualCostUsd?: number | null
  costSource?: string | null
}

/**
 * The one cost-selection order every margin surface must use:
 * `providerBilledCostUsd` → `outputMeasuredCostUsd` → `estimatedCostUsd`.
 *
 * Returns the source alongside the dollars because a margin number without its provenance is the
 * exact overread this work exists to kill — an estimate must never be displayed as an actual.
 *
 * Legacy rows carry the output-measured dollars in `actualCostUsd` with no
 * `outputMeasuredCostUsd`; those only count as output-measured when `costSource` says they were
 * measured. An `actualCostUsd` written alongside `REGISTRY_ESTIMATE` is an estimate and is graded
 * as one.
 */
export const selectJobCostUsd = (job: IJobCostView): { costUsd: number | null; source: ECostSource | null } => {
  if (job.providerBilledCostUsd != null) {
    return { costUsd: job.providerBilledCostUsd, source: ECostSource.PROVIDER_BILLED }
  }

  const outputMeasured =
    job.outputMeasuredCostUsd ?? (isOutputMeasuredCostSource(job.costSource) ? job.actualCostUsd : null)
  if (outputMeasured != null) {
    return { costUsd: outputMeasured, source: ECostSource.OUTPUT_MEASURED }
  }

  // Inside the estimate tier, a legacy `actualCostUsd` still wins over `estimatedCostUsd`: both are
  // registry-rate figures, but the former was computed against a known output quantity. Grading it
  // as an estimate is about the LABEL being honest, not about throwing away the better number.
  const estimated = job.actualCostUsd ?? job.estimatedCostUsd
  if (estimated != null) {
    return { costUsd: estimated, source: ECostSource.REGISTRY_ESTIMATE }
  }

  return { costUsd: null, source: null }
}

export interface ChannelRate {
  multiplier: number
}

export const CHANNEL_RATES: Record<EChannelCostType, ChannelRate> = {
  [EChannelCostType.API]: { multiplier: 1.2 },
  [EChannelCostType.TOOL]: { multiplier: 1.5 },
  [EChannelCostType.WORKFLOW]: { multiplier: 0 }, // price set on each workflow
}

/* ---------- 3️⃣  Defined Workflows (public-facing) ---------- */

/**
 * type: WORKFLOW / TOOL / API
 * subtype (WorkflowType/ToolType/APIType): FAKE_TEXT_TEMPLATE / IMAGE_GENERATION / IMAGE_GENERATION
 * misc.model: Kling-1.6-Std / Kling-1.6-Pro / Kling-1.6-Pro-2 / Kling-1.6-Pro-3 / Kling-1.6-Pro-4 / Kling-1.6-Pro-5 / Kling-1.6-Pro-6 / Kling-1.6-Pro-7 / Kling-1.6-Pro-8 / Kling-1.6-Pro-9 / Kling-1.6-Pro-10
 */

export type EUsageLogType = EChannelCostType | EUsageAddType

export enum EDefinedWorkflow {
  FAKE_TEXT_TEMPLATE = "FAKE_TEXT_TEMPLATE",
  AI_STORY_TEMPLATE = "AI_STORY_TEMPLATE",
  REDDIT_STORY_TEMPLATE = "REDDIT_STORY_TEMPLATE",
  UGC_TEMPLATE = "UGC_TEMPLATE",
}

// Legacy - to be deprecated eventually
export enum VMToolType {
  IMAGE_GENERATION = "IMAGE_GENERATION",
  VIDEO_ANIMATION = "VIDEO_ANIMATION",
  SCRIPT_REWRITE = "SCRIPT_REWRITE",
  VOICE_GENERATION = "VOICE_GENERATION",
  RENDERING = "RENDERING",
  BASIC_VIDEO_GENERATION = "BASIC_VIDEO_GENERATION",
}
export const ACTION_COSTS = {
  [VMToolType.IMAGE_GENERATION]: 1,
  [VMToolType.VIDEO_ANIMATION]: 10,
  [VMToolType.SCRIPT_REWRITE]: 1,
  [VMToolType.VOICE_GENERATION]: 1,
  [VMToolType.RENDERING]: 1,
  [VMToolType.BASIC_VIDEO_GENERATION]: 10,
} as const

export interface DefinedWorkflowMeta {
  /** Display name shown in the UI */
  name: string
  /** All-inclusive price the user pays (credits or "spark points") */
  flatCredits: number
  /** Uses the WORKFLOW channel rate for ledger classification */
  channelRate: EChannelCostType
  /** Optional description / marketing copy */
  blurb?: string
}

export const DEFINED_WORKFLOWS: Record<EDefinedWorkflow, DefinedWorkflowMeta> = {
  [EDefinedWorkflow.FAKE_TEXT_TEMPLATE]: {
    name: "Fake Text Template",
    flatCredits: 10,
    channelRate: EChannelCostType.WORKFLOW,
    blurb: "Create a stylised text-only teaser clip in under 60 s.",
  },

  [EDefinedWorkflow.AI_STORY_TEMPLATE]: {
    name: "AI Story Template",
    flatCredits: 10,
    channelRate: EChannelCostType.WORKFLOW,
    blurb: "Generate a 15-second visual story with voice-over.",
  },

  [EDefinedWorkflow.REDDIT_STORY_TEMPLATE]: {
    name: "Reddit Story Template",
    flatCredits: 10,
    channelRate: EChannelCostType.WORKFLOW,
    blurb: "Generate a reddit story template.",
  },

  [EDefinedWorkflow.UGC_TEMPLATE]: {
    name: "UGC Template",
    flatCredits: 20,
    channelRate: EChannelCostType.WORKFLOW,
    blurb: "Generate a UGC ad video from a talking-avatar template.",
  },
}

/* ---------- 4️⃣  Helper – debit credits for a workflow ---------- */

export function debitWorkflowCredits(wf: EDefinedWorkflow, _userId: string): number {
  const meta = DEFINED_WORKFLOWS[wf]
  // ledger-service call here…
  return meta.flatCredits
}

// Helper function to convert USD to base credits using the global rate
const calculateBaseCredits = (usd: number): number => {
  // Removed usdPerCredit parameter, using global USD_PER_CREDIT directly
  if (USD_PER_CREDIT <= 0) {
    blg.warn("[calculateBaseCredits] USD_PER_CREDIT is zero or negative, returning 0 credits.")
    return 0
  }
  return usd / USD_PER_CREDIT
}

export const calculateWorkflowToolCost = (subtype: VMToolType | EDefinedWorkflow, quantity = 1): number => {
  let baseCost = ACTION_COSTS[subtype]

  if (!baseCost) {
    const workflowCost = DEFINED_WORKFLOWS[subtype]
    baseCost = workflowCost.flatCredits
  }

  if (!baseCost) {
    throw BadDataError(`Invalid subtype type: ${subtype}`)
  }

  // For quantity-based subtypes
  return quantity * baseCost
}

/* quoting */
// Removed old quote function as quoteAllChannels replaces it

/* quoting for all channels */
// Simplified generic signature to aid inference
/**
 * Resolve the per-unit rate from the COMMERCIAL registry when that model prices by tier.
 *
 * `MODEL_METADATA.ratesUSD` can only hold one scalar per rate key, so every tiered model was
 * flattened to a single number and the admin quote silently understated the real charge:
 * Wan 2.5 quoted $0.10/s for a 1080p render that bills $0.15, Seedance-1-Pro quoted its 480p
 * $0.03 for a $0.15 1080p job, and Seedance-1-Lite's $0.016 matched no tier at all — 480p is
 * $0.036. Gemini Omni Flash has the same problem on a different axis (t2v/i2v/r2v/v2v).
 *
 * The commercial registry already expresses these as `rate(params)`, so ask it rather than keeping
 * a second, lossy copy. Metering still owns the SHAPE (which keys meter, which params validate);
 * the commercial registry owns the NUMBER.
 *
 * Only substituted where the two agree on what a unit IS — a per-second metering entry against a
 * PER_SECOND commercial entry, or a per-quantity one against PER_IMAGE/PER_REQUEST. `gpt-image-1`
 * meters in tokens against a per-image commercial rate, so it is deliberately left alone.
 */
const tieredRatesUSD = (
  modelId: string,
  meta: EngineMeta<any>,
  params: Record<string, unknown>,
): Partial<Record<RateKey, number>> => {
  const entry = (replicateModelPricing as Record<string, CommercialEntry | undefined>)[modelId]
  const imageEntry = (replicateImageModelPricing as Record<string, CommercialEntry | undefined>)[modelId]
  const commercial = entry ?? imageEntry
  if (!commercial || typeof commercial.rate !== "function") return meta.ratesUSD

  const rateKey: RateKey | null =
    meta.unit === "seconds" && commercial.unit === "per_second"
      ? "secondsOutput"
      : meta.unit === "quantity" && (commercial.unit === "per_image" || commercial.unit === "per_request")
        ? "imageOutput"
        : null
  if (!rateKey || meta.ratesUSD[rateKey] === undefined) return meta.ratesUSD

  try {
    const resolved = commercial.rate(params)
    if (typeof resolved !== "number" || !Number.isFinite(resolved)) return meta.ratesUSD
    return { ...meta.ratesUSD, [rateKey]: resolved }
  } catch {
    // Params that the tier function cannot read fall back to the flat rate rather than throwing —
    // a quote with a stale number still beats a 500 on the admin page.
    return meta.ratesUSD
  }
}

export function quoteAllChannels<M extends AllSupportedModelTypes>(
  modelId: M,
  params: EngineMeta<any>["meter"] extends (p: infer P) => any ? P : never, // Infer params type
) {
  const meta = MODEL_METADATA[modelId] // Access directly, type is inferred

  if (!meta) {
    throw BadDataError(`Model ID "${modelId}" not found in metadata.`) // Removed 'new'
  }

  let safeParams: typeof params
  try {
    safeParams = meta.paramSchema.parse(params)
  } catch (error) {
    if (error instanceof ZodError) {
      throw BadDataError(
        // Removed 'new'
        `Invalid parameters for model ${modelId}: ${error.errors
          .map((e) => `${e.path.join(".")}: ${e.message}`)
          .join(", ")}`,
      )
    }
    throw error
  }

  const usage = meta.meter(safeParams)
  // RAW params, not `safeParams`: each metering schema is a closed `z.object`, so parsing STRIPS
  // every key it does not model — including the `resolution` / `image_url` fields the tier
  // functions select on. Passing the parsed object silently handed every tiered model an empty
  // params bag and it fell back to its default tier, which is the bug this resolver exists to fix.
  const totalUsd = sumUsageToUsd(usage, tieredRatesUSD(modelId, meta, params as Record<string, unknown>))
  // Use the global USD_PER_CREDIT via the updated helper
  const baseCred = calculateBaseCredits(totalUsd) // Removed meta.usdPerCredit

  const billedCredits = mapValues(CHANNEL_RATES, (channelRate) => {
    return baseCred * channelRate.multiplier
  })

  return {
    units: usage,
    usd: totalUsd,
    baseCredits: baseCred,
    billedCreditsPerChannel: billedCredits,
  }
}

// Helper function to calculate total USD from usage units and rates
const sumUsageToUsd = (usage: RateUsage, ratesUSD: Partial<Record<RateKey, number>>): number => {
  // Use reduce directly on usage object entries
  return reduce(
    usage,
    (sum, units, key) => {
      const rate = ratesUSD[key as RateKey] ?? 0
      // Ensure units is a number before calculation
      const numericUnits = typeof units === "number" ? units : 0
      // Rates are per MILLION units, so divide units by 1M
      return sum + numericUnits * rate
    },
    0,
  )
}
